import { isPlatformWeb } from 'renative';
import isEmpty from 'lodash/isEmpty';

import {
    Asset,
    ASSET_TYPE,
    getIsNewsSeries,
    getIsPodcastEpisode,
    getIsPodcastSeries,
    NewsSeries,
    PageSection,
} from '@24i/nxg-sdk-photon';
import { useCast } from '@24i/nxg-sdk-smartott/src/context/Cast';
import prepareAutoPlay from '@24i/nxg-sdk-smartott/src/utils/prepareAutoPlay';
import { useContentData } from '@24i/nxg-sdk-smartott-shared/src/context/ContentData';
import { usePlayerSourceManager } from '@24i/nxg-sdk-smartott-shared';
import { dynarow } from '@24i/nxg-sdk-smartott-shared/src/hooks/query';
import useSectionsStructureQuery from '@24i/nxg-sdk-smartott-shared/src/hooks/query/dynarow/useSectionsStructureQuery';
import { useRefreshOnFocus } from '@24i/nxg-sdk-smartott-shared/src/hooks/useRefreshOnFocus';

import useAssetActions from '../../../hooks/useAssetActions';
import { useStore } from '../../../context/ApplicationStore';
import { CustomAsset, DynaRowSharedProps, DynaRowScreenProps } from '../types';
import useAssetBlockersValidation from '../../../hooks/useAssetBlockersValidation';
import { usePodcastPlayer } from '../../../context/PodcastPlayerProvider';

const PLAYLISTS_MOBILE_PAGE_SIZE = 3;

const useShared = ({
    goToPlayer: customGoToPlayer,
    pageId = '',
    sections: sectionsFromProps,
    originFromHere,
}: DynaRowScreenProps): DynaRowSharedProps => {
    const { selectedProfile } = useStore();
    const { getStream } = usePlayerSourceManager();
    const contentDataClient = useContentData();

    const { startPlayback, isCanGoToPlayer, isCanGoToDetail, fetchEpisodeToReproduce } =
        useAssetActions({
            origin: originFromHere,
        });
    const { isCastConnected, startCastingStream } = useCast();
    const { handleBlockersCheck } = useAssetBlockersValidation({});
    const {
        closePlayer: closePodcastPlayer,
        startLoading: startLoadingPodcastPlayer,
        stopLoading: stopLoadingPodcastPlayer,
        episodeInPlayer: podcastEpisodeInPlayer,
    } = usePodcastPlayer();

    // If sections are delivered from props, there is no need to fetch data through pageId. Placing it an empty string will disable this and following request.
    // REPLY -> This should be improved. React query accepts initialData as an arg for a query. If data is present React Query should pull data from there.
    const shouldFetchData = !sectionsFromProps;
    const pageIdToFetch = shouldFetchData ? pageId : '';

    const {
        data: pageSections,
        isError: isPageSectionsError,
        isLoading: arePageSectionsLoading,
        isIdle: arePageSectionsIdle,
    } = useSectionsStructureQuery(pageIdToFetch, selectedProfile);

    // TODO - Legacy logic, why do we have different behavior for web and mobile?
    const playListSize = isPlatformWeb ? pageSections?.length : PLAYLISTS_MOBILE_PAGE_SIZE;

    const {
        data: sectionsWithPlaylistsPages,
        isError: isPlaylistsError,
        isLoading: isPlaylistLoading,
        isIdle: isPlaylistIdle,
        fetchNextPage,
        refetch,
    } = dynarow.usePlaylistsQuery(pageSections || [], pageId, playListSize, selectedProfile);

    const sectionsFromQuery = sectionsWithPlaylistsPages;
    const sections: PageSection[] | undefined =
        sectionsFromProps || sectionsFromQuery?.pages?.flat();

    let isDataLoading =
        arePageSectionsLoading || arePageSectionsIdle || isPlaylistLoading || isPlaylistIdle;

    // If data is already present in sections variable it means rows and playlist content has been fetched already, at least partially so we want to render.
    if (!isEmpty(sections)) isDataLoading = false;

    const handleGoToPlayer = async (
        item: Asset | CustomAsset,
        handlePurchaseAsset?: (asset: Asset) => void,
        playPodcastFromDetailPage?: boolean,
        redirectToPodcastDetailPage?: (item: Asset) => void
    ) => {
        if (customGoToPlayer) {
            customGoToPlayer(item);
            return;
        }

        const isPodcastSeries = getIsPodcastSeries(item);
        const isPodcastEpisode = getIsPodcastEpisode(item);
        const isLiveBroadcast = Boolean(
            item.type === ASSET_TYPE.BROADCAST && item.isLive && item.channel
        );

        const shouldFollowPodcastFlow = isPodcastSeries || isPodcastEpisode;

        let assetToCheck = item;
        if (isPodcastSeries) {
            assetToCheck = (await fetchEpisodeToReproduce(item, 'latest')) || assetToCheck;
        }
        if (isLiveBroadcast) {
            assetToCheck = {
                ...item.channel,
                type: ASSET_TYPE.CHANNEL,
                id: item.channelId || item.channel?.id || item.id,
            };
        }

        const { hasBlocker, MVPCustomMessage } = await handleBlockersCheck({
            asset: assetToCheck ?? item,
            handlePurchase: handlePurchaseAsset,
            openAPTModalOnBlock: false,
        });

        if (hasBlocker && !MVPCustomMessage) {
            if (shouldFollowPodcastFlow) closePodcastPlayer();
            return; // allow Adobe Primetime block cases to arrive to playbackScreen and show modal there
        }

        if (shouldFollowPodcastFlow) {
            const episodeIsAlreadyInPodcastPlayer = podcastEpisodeInPlayer?.id === assetToCheck?.id;
            if (episodeIsAlreadyInPodcastPlayer) {
                return;
            }

            if (
                isPlatformWeb &&
                playPodcastFromDetailPage &&
                redirectToPodcastDetailPage &&
                assetToCheck &&
                getIsPodcastEpisode(assetToCheck)
            ) {
                redirectToPodcastDetailPage(item);
                return;
            }

            closePodcastPlayer();
            startLoadingPodcastPlayer();
        }

        if (isCastConnected) {
            const { source } = await getStream(item, { isCastConnected });
            await startCastingStream(item, source);
        } else {
            if (shouldFollowPodcastFlow) stopLoadingPodcastPlayer();
            await prepareAutoPlay();
            startPlayback(item);
        }
    };

    const playLatestNewsEpisode = async (
        newsSeries: NewsSeries,
        handlePurchaseAsset?: (asset: Asset) => void
    ) => {
        const latestNewsEpisode = await contentDataClient.fetchLatestNewsEpisode(newsSeries.id);
        if (latestNewsEpisode) handleGoToPlayer(latestNewsEpisode, handlePurchaseAsset);
    };

    const handleSeriesPlayerAction = async (
        show: Asset,
        handlePurchaseAsset?: (asset: Asset) => void
    ) => {
        const isNewsSeries = getIsNewsSeries(show);

        if (isNewsSeries) {
            playLatestNewsEpisode(show, handlePurchaseAsset);
            return;
        }

        const episode = await fetchEpisodeToReproduce(show, 'first');
        let seriesTitle;
        let episodeWithSeriesTitle;
        if (!show.isTrailer) {
            seriesTitle = show.title;
            episodeWithSeriesTitle = {
                ...episode,
                seriesTitle,
            };
        }
        if (!episode) return;
        handleGoToPlayer(episodeWithSeriesTitle, handlePurchaseAsset);
    };

    const onPackshotPress = async ({
        onLinkPress,
        onGoToDetails,
        section,
        asset,
        onPurchaseAsset,
        playPodcastFromDetailPage,
        redirectToPodcastDetailPage,
    }: {
        onLinkPress: () => void;
        onGoToDetails: () => void;
        section: PageSection;
        asset: Asset;
        onPurchaseAsset?: (asset: Asset) => void;
        playPodcastFromDetailPage?: boolean;
        redirectToPodcastDetailPage?: (item: Asset) => void;
    }) => {
        if ([ASSET_TYPE.CUSTOM, ASSET_TYPE.IMAGE].includes(asset.type)) {
            onLinkPress();
        } else if (
            section.actionForAllItems === 'player' &&
            (asset.type === ASSET_TYPE.SERIES || asset.type === ASSET_TYPE.NEWS_SERIES)
        ) {
            handleSeriesPlayerAction(asset, onPurchaseAsset);
        } else {
            let shouldGoToPlayer: boolean;
            if (section.actionForAllItems === 'player') {
                shouldGoToPlayer = isCanGoToPlayer(asset) || !isCanGoToDetail(asset);
            } else if (section.actionForAllItems === 'detail') {
                // @ts-ignore TODO: Should cast item to broadcast ?
                const isChannelWithBroadcast = asset.type === ASSET_TYPE.CHANNEL; // && asset.broadcastMetadata?.id;
                shouldGoToPlayer =
                    !isCanGoToDetail(asset) && isCanGoToPlayer(asset) && !isChannelWithBroadcast;
            } else {
                shouldGoToPlayer = !isCanGoToDetail(asset) && isCanGoToPlayer(asset);
            }

            if (shouldGoToPlayer) {
                handleGoToPlayer(
                    asset,
                    onPurchaseAsset,
                    playPodcastFromDetailPage,
                    redirectToPodcastDetailPage
                );
            } else {
                onGoToDetails();
            }
        }
    };

    useRefreshOnFocus(refetch);

    return {
        handleGoToPlayer,
        fetchNextPage: () => fetchNextPage(),
        refetch,
        onPackshotPress,
        hasError: isPageSectionsError || isPlaylistsError,
        sections,
        isLoading: isDataLoading,
    };
};

export default useShared;
